import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Grid, Row, Col } from 'react-bootstrap';
import '../Styles/responsible-tourism.css';

export default class KeralaResponsibleTourism extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'overview'
    };
  }

  render() {
    const initiatives = [
      {
        icon: "fas fa-users",
        title: "Community Empowerment",
        description: "Supporting local communities through employment, skill development, and fair wages",
        impact: "500+ families benefited"
      },
      {
        icon: "fas fa-leaf",
        title: "Eco-friendly Practices",
        description: "Implementing sustainable operations, waste management, and energy conservation",
        impact: "40% reduction in plastic usage"
      },
      {
        icon: "fas fa-hand-holding-heart",
        title: "Cultural Preservation",
        description: "Protecting and promoting traditional art forms, crafts, and heritage",
        impact: "20+ art forms preserved"
      },
      {
        icon: "fas fa-tree",
        title: "Wildlife Conservation",
        description: "Protecting natural habitats and supporting wildlife conservation efforts",
        impact: "1000+ trees planted"
      }
    ];

    const kairalyInitiatives = [
      {
        title: "Local Employment Generation",
        description: "We prioritize hiring local staff, guides, and drivers, ensuring that tourism benefits flow directly to local communities.",
        stats: "85% local employment rate"
      },
      {
        title: "Eco-friendly Houseboats",
        description: "Our houseboats use solar power, LED lighting, and eco-friendly waste management systems to minimize environmental impact.",
        stats: "30% carbon footprint reduction"
      },
      {
        title: "Plastic-Free Initiative",
        description: "We've eliminated single-use plastics across all our operations and promote reusable alternatives.",
        stats: "1,00,000+ plastic bottles saved annually"
      },
      {
        title: "Local Product Promotion",
        description: "We source local products, handicrafts, and organic produce to support local artisans and farmers.",
        stats: "200+ local artisans supported"
      },
      {
        title: "Responsible Wildlife Tourism",
        description: "We promote ethical wildlife viewing practices and support conservation efforts in wildlife sanctuaries.",
        stats: "5 wildlife programs supported"
      },
      {
        title: "Water Conservation",
        description: "Implementation of rainwater harvesting and water recycling systems in our partner hotels.",
        stats: "1 million+ liters water saved"
      }
    ];

    const responsiblePractices = [
      {
        title: "Waste Management",
        practices: [
          "Segregation of waste at source",
          "Composting organic waste",
          "Recycling programs for plastics and glass",
          "Partnering with local recycling units"
        ]
      },
      {
        title: "Energy Conservation",
        practices: [
          "Solar water heating systems",
          "LED lighting throughout",
          "Energy-efficient appliances",
          "Sensor-based lighting in common areas"
        ]
      },
      {
        title: "Water Conservation",
        practices: [
          "Rainwater harvesting systems",
          "Greywater recycling for gardening",
          "Low-flow fixtures in bathrooms",
          "Guest awareness programs"
        ]
      },
      {
        title: "Community Engagement",
        practices: [
          "Village homestay programs",
          "Local craft workshops",
          "Traditional cooking classes",
          "Cultural performance evenings"
        ]
      }
    ];

    const keralaInitiatives = [
      {
        name: "Responsible Tourism Mission",
        description: "Government initiative promoting sustainable tourism practices across Kerala",
        website: "www.keralatourism.org/responsible-tourism/"
      },
      {
        name: "Green Leaf Certification",
        description: "Eco-certification program for hotels and houseboats implementing sustainable practices",
        website: "glr.suchitwamission.org/"
      },
      {
        name: "SAATHI Project",
        description: "Empowering local communities through tourism-related employment and skill development",
        website: "www.saathi.kerala.gov.in"
      },
      {
        name: "Haritha Kerala Mission",
        description: "State-wide initiative for waste management and environmental protection",
        website: "www.harithakeralamission.gov.in"
      }
    ];

    return (
      <div className="responsible-tourism-page">
        {/* Hero Section */}
        <div className="rt-hero">
          <div className="rt-hero-overlay"></div>
          <div className="rt-hero-content">
            <div className="rt-hero-badge">
              <span>Responsible Tourism</span>
            </div>
            <h1 className="rt-hero-title">Travel Responsibly<br />with Kairaly</h1>
            <p className="rt-hero-subtitle">
              Committed to sustainable tourism that benefits local communities, 
              preserves nature, and protects cultural heritage
            </p>
            <div className="rt-hero-stats">
              <div className="stat">
                <span className="number">10+</span>
                <span className="label">Years of RT Practice</span>
              </div>
              <div className="stat">
                <span className="number">500+</span>
                <span className="label">Local Families Supported</span>
              </div>
              <div className="stat">
                <span className="number">40%</span>
                <span className="label">Carbon Reduction</span>
              </div>
            </div>
          </div>
          <div className="rt-hero-wave">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
              <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="rt-intro">
          <Grid>
            <Row>
              <Col xs={12} md={6} className="rt-intro-text">
                <h2 className="rt-section-title">What is Responsible Tourism?</h2>
                <p className="rt-intro-description">
                  Responsible Tourism is about making better places for people to live in and 
                  better places for people to visit. It's a commitment to sustainable travel 
                  that minimizes negative impacts and maximizes positive benefits for local 
                  communities, cultures, and the environment.
                </p>
                <p className="rt-intro-description">
                  Kerala has been a pioneer in responsible tourism, being recognized by the 
                  UNWTO for its innovative approaches. The state's Responsible Tourism Mission 
                  has transformed the way tourism operates, ensuring that local communities 
                  are at the heart of development.
                </p>
                <div className="rt-highlight-box">
                  <i className="fas fa-trophy"></i>
                  <span>Kerala won the UNWTO Ulysses Award for Innovation in Responsible Tourism</span>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="rt-intro-image">
                  <img src="/assets/img/responsible-tourism-kerala.jpg" alt="Responsible Tourism Kerala" />
                  <div className="image-caption">Kerala - A model for Responsible Tourism</div>
                </div>
              </Col>
            </Row>
          </Grid>
        </div>

        {/* Core Initiatives Section */}
        <div className="rt-initiatives">
          <Grid>
            <div className="section-header">
              <h2 className="rt-section-title-centered">Core Initiatives</h2>
              <p className="rt-section-subtitle">
                Our commitment to sustainable and responsible tourism
              </p>
            </div>
            <Row className="rt-initiatives-grid">
              {initiatives.map((initiative, index) => (
                <Col xs={12} sm={6} md={3} key={index} className="rt-initiative-col">
                  <div className="rt-initiative-card">
                    <div className="initiative-icon">
                      <i className={initiative.icon}></i>
                    </div>
                    <h3 className="initiative-title">{initiative.title}</h3>
                    <p className="initiative-description">{initiative.description}</p>
                    <div className="initiative-impact">
                      <i className="fas fa-chart-line"></i>
                      <span>{initiative.impact}</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Grid>
        </div>

        {/* Kairaly's Role Section */}
        <div className="rt-kairaly-role">
          <div className="rt-kairaly-overlay"></div>
          <Grid>
            <Row>
              <Col xs={12}>
                <div className="rt-kairaly-header">
                  <h2 className="rt-section-title-white">Kairaly Hotels & Holidays</h2>
                  <p className="rt-section-subtitle-white">
                    Our Commitment to Responsible Tourism
                  </p>
                </div>
              </Col>
            </Row>
            <Row className="rt-kairaly-grid">
              {kairalyInitiatives.map((initiative, index) => (
                <Col xs={12} sm={6} md={4} key={index} className="rt-kairaly-col">
                  <div className="rt-kairaly-card">
                    <div className="kairaly-card-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <h3 className="kairaly-card-title">{initiative.title}</h3>
                    <p className="kairaly-card-description">{initiative.description}</p>
                    <div className="kairaly-card-stats">
                      <i className="fas fa-chart-simple"></i>
                      <span>{initiative.stats}</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Grid>
        </div>

        {/* Responsible Practices Section */}
        <div className="rt-practices">
          <Grid>
            <div className="section-header">
              <h2 className="rt-section-title-centered">Our Responsible Practices</h2>
              <p className="rt-section-subtitle">
                Implementing sustainable practices across all operations
              </p>
            </div>
            <Row className="rt-practices-grid">
              {responsiblePractices.map((practice, index) => (
                <Col xs={12} sm={6} md={3} key={index} className="rt-practice-col">
                  <div className="rt-practice-card">
                    <h3 className="practice-title">{practice.title}</h3>
                    <ul className="practice-list">
                      {practice.practices.map((item, idx) => (
                        <li key={idx}>
                          <i className="fas fa-check"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </Grid>
        </div>

        {/* Kerala Government Initiatives */}
        <div className="rt-government">
          <Grid>
            <div className="section-header">
              <h2 className="rt-section-title-centered">Kerala's Responsible Tourism Initiatives</h2>
              <p className="rt-section-subtitle">
                Government programs supporting sustainable tourism
              </p>
            </div>
            <Row className="rt-government-grid">
              {keralaInitiatives.map((initiative, index) => (
                <Col xs={12} sm={6} md={3} key={index} className="rt-government-col">
                  <div className="rt-government-card">
                    <div className="government-icon">
                      <i className="fas fa-building"></i>
                    </div>
                    <h4 className="government-name">{initiative.name}</h4>
                    <p className="government-description">{initiative.description}</p>
                    <a href={`https://${initiative.website}`} target="_blank" rel="noopener noreferrer" className="government-link">
                      <i className="fas fa-external-link-alt"></i> Learn More
                    </a>
                  </div>
                </Col>
              ))}
            </Row>
          </Grid>
        </div>

        {/* Impact Section */}
        <div className="rt-impact">
          <Grid>
            <Row>
              <Col xs={12}>
                <div className="rt-impact-header">
                  <h2 className="rt-section-title-white">Our Impact</h2>
                  <p className="rt-section-subtitle-white">
                    Measurable results of our responsible tourism efforts
                  </p>
                </div>
              </Col>
            </Row>
            <Row className="rt-impact-stats">
              <Col xs={12} sm={6} md={3} className="rt-impact-col">
                <div className="rt-impact-card">
                  <div className="impact-number">85%</div>
                  <div className="impact-label">Local Employment</div>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-impact-col">
                <div className="rt-impact-card">
                  <div className="impact-number">40%</div>
                  <div className="impact-label">Plastic Reduction</div>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-impact-col">
                <div className="rt-impact-card">
                  <div className="impact-number">1M+</div>
                  <div className="impact-label">Liters Water Saved</div>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-impact-col">
                <div className="rt-impact-card">
                  <div className="impact-number">1000+</div>
                  <div className="impact-label">Trees Planted</div>
                </div>
              </Col>
            </Row>
          </Grid>
        </div>

        {/* How You Can Contribute Section */}
        <div className="rt-contribute">
          <Grid>
            <div className="section-header">
              <h2 className="rt-section-title-centered">How You Can Contribute</h2>
              <p className="rt-section-subtitle">
                Join us in our journey towards responsible tourism
              </p>
            </div>
            <Row className="rt-contribute-grid">
              <Col xs={12} sm={6} md={3} className="rt-contribute-col">
                <div className="rt-contribute-card">
                  <div className="contribute-icon">
                    <i className="fas fa-recycle"></i>
                  </div>
                  <h4>Reduce Plastic</h4>
                  <p>Carry reusable water bottles and bags. Avoid single-use plastics.</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-contribute-col">
                <div className="rt-contribute-card">
                  <div className="contribute-icon">
                    <i className="fas fa-leaf"></i>
                  </div>
                  <h4>Respect Nature</h4>
                  <p>Don't litter, stay on designated trails, and avoid disturbing wildlife.</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-contribute-col">
                <div className="rt-contribute-card">
                  <div className="contribute-icon">
                    <i className="fas fa-hand-sparkles"></i>
                  </div>
                  <h4>Support Local</h4>
                  <p>Buy local products, eat at local restaurants, and use local guides.</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="rt-contribute-col">
                <div className="rt-contribute-card">
                  <div className="contribute-icon">
                    <i className="fas fa-water"></i>
                  </div>
                  <h4>Conserve Water</h4>
                  <p>Be mindful of water usage, especially in eco-sensitive areas.</p>
                </div>
              </Col>
            </Row>
          </Grid>
        </div>

        {/* CTA Section */}
        <div className="rt-cta">
          <div className="rt-cta-content">
            <h3>Travel Responsibly with Kairaly</h3>
            <p>Choose sustainable travel options that make a difference</p>
            <Link to="/contact" className="rt-cta-button">
              Plan Your Responsible Trip
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>
    );
  }
}